import {
  Goal,
  goalsArchivedSectionID,
  ID,
  rootId,
  TaskListItemTypes,
  TaskListTreeItem,
} from '@griply/types';

import {
  getArchivedGoalIds,
  getCompletedGoalIds,
} from '../../derived-goal-state';
import {
  compareDeadlineQuarterKeys,
  getDeadlineQuarterKey,
} from '../deadline-quarters';
import { getNearestPresentParentId } from '../get-nearest-present-parent-id';
import { BuildGoalListTreeOptions } from './types';

// Quarter sections use the quarter key (e.g. '2026-Q1' / 'no-deadline') directly
// as their tree id; goal ids are Firestore ids and never collide with these. The
// section node `name` is the raw key — consumers resolve the display label.
export const buildDeadlineGoalListTree = (
  goals: Goal[],
  {
    hideArchived,
    hideCompleted,
    archivedSectionName,
    allGoals,
  }: BuildGoalListTreeOptions,
): Record<ID, TaskListTreeItem> => {
  const archivedIds = getArchivedGoalIds(allGoals);
  const completedIds = getCompletedGoalIds(allGoals);
  const activeGoals: Goal[] = [];
  const archivedGoals: ID[] = [];

  goals.forEach((goal) => {
    if (hideArchived && archivedIds.has(goal.id)) {
      return;
    }
    if (goal.archivedAt) {
      archivedGoals.push(goal.id);
      return;
    }
    if (!hideCompleted || !completedIds.has(goal.id)) {
      activeGoals.push(goal);
    }
  });

  const treeItems: Record<ID, TaskListTreeItem> = {};

  goals.forEach((goal) => {
    treeItems[goal.id] = {
      id: goal.id,
      type: TaskListItemTypes.Goal,
      name: goal.name,
      children: [],
      isFolder: true,
    };
  });

  const quarterKeys = new Set<string>();

  activeGoals.forEach((goal) => {
    const parentId = getNearestPresentParentId(goal, (id) => !!treeItems[id]);

    if (parentId) {
      treeItems[parentId].children?.push(goal.id);
      return;
    }

    // Archived through an ancestor the filter dropped — no parent left to nest under.
    if (archivedIds.has(goal.id)) {
      archivedGoals.push(goal.id);
      return;
    }

    const quarterKey = getDeadlineQuarterKey(goal.deadline);
    if (!treeItems[quarterKey]) {
      treeItems[quarterKey] = {
        id: quarterKey,
        type: TaskListItemTypes.LifeArea,
        name: quarterKey,
        children: [],
        isFolder: true,
      };
      quarterKeys.add(quarterKey);
    }
    treeItems[quarterKey].children?.push(goal.id);
  });

  if (archivedGoals.length) {
    treeItems[goalsArchivedSectionID] = {
      id: goalsArchivedSectionID,
      type: TaskListItemTypes.LifeArea,
      name: archivedSectionName,
      children: archivedGoals,
      isFolder: true,
    };
  }

  const rootChildren = [...quarterKeys].sort(compareDeadlineQuarterKeys);

  if (archivedGoals.length) {
    rootChildren.push(goalsArchivedSectionID);
  }

  return {
    [rootId]: {
      id: rootId,
      name: 'Root',
      type: TaskListItemTypes.Root,
      children: rootChildren,
    },
    ...treeItems,
  };
};
